import { useEffect, useState } from 'react';
import { acknowledgeFlaggedCustomer } from '@/lib/api/customer-status-upsell.js';
import { CustomerStatusBadge } from '@/components/common/CustomerStatusBadge.js';

type CustomerLite = {
  id: string;
  firstName?: string;
  lastName?: string | null;
  customerStatus?: string | null;
  statusReason?: string | null;
};

type Props = {
  customer: CustomerLite | null | undefined;
  context: string;
  vipLabel?: string | null | undefined;
  flaggedLabel?: string | null | undefined;
  onFlagAcknowledged?: () => void;
  /** When true, blocks parent continue until flagged is acknowledged */
  requireAck?: boolean;
};

/** VIP toast (auto-dismiss) + blocking flagged acknowledgment modal for ticket create flows. */
export function CustomerStatusGate({
  customer,
  context,
  vipLabel,
  flaggedLabel,
  onFlagAcknowledged,
  requireAck = true,
}: Props) {
  const status = customer?.customerStatus ?? 'normal';
  const [showVip, setShowVip] = useState(false);
  const [showFlag, setShowFlag] = useState(false);
  const [acking, setAcking] = useState(false);

  useEffect(() => {
    if (!customer?.id) {
      setShowVip(false);
      setShowFlag(false);
      return;
    }
    if (status === 'vip') {
      setShowVip(true);
      setShowFlag(false);
      const t = window.setTimeout(() => setShowVip(false), 4000);
      return () => window.clearTimeout(t);
    }
    if (status === 'flagged') {
      setShowFlag(true);
      setShowVip(false);
      return;
    }
    setShowVip(false);
    setShowFlag(false);
  }, [customer?.id, status]);

  async function acknowledge() {
    if (!customer?.id) return;
    setAcking(true);
    try {
      await acknowledgeFlaggedCustomer(customer.id, context);
      setShowFlag(false);
      onFlagAcknowledged?.();
    } finally {
      setAcking(false);
    }
  }

  const name = [customer?.firstName, customer?.lastName].filter(Boolean).join(' ') || 'Customer';

  return (
    <>
      {showVip ? (
        <div className="mb-3 flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <div className="flex items-center gap-2">
            <CustomerStatusBadge status="vip" vipLabel={vipLabel ?? null} size="md" />
            <span>
              {name} is marked {vipLabel || 'VIP'}. Provide excellent service.
            </span>
          </div>
          <button
            type="button"
            className="shrink-0 font-medium text-amber-900 underline"
            onClick={() => setShowVip(false)}
          >
            Got it
          </button>
        </div>
      ) : null}

      {showFlag && requireAck ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-3 flex items-center gap-2">
              <CustomerStatusBadge status="flagged" flaggedLabel={flaggedLabel ?? null} size="md" />
              <h2 className="text-lg font-semibold text-slate-900">Flagged customer</h2>
            </div>
            <p className="text-sm text-slate-600">
              {name} is flagged
              {customer?.statusReason ? `: ${customer.statusReason}` : '.'} Acknowledge before
              continuing with this ticket.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                disabled={acking}
                onClick={() => void acknowledge()}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                {acking ? 'Saving…' : 'I acknowledge'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
